import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, relTime, type CreatedWebhook } from '../lib/api'

/**
 * Webhook endpoints.
 *
 * Delivery events are asynchronous: a receiver can accept a message and bounce it minutes
 * later. Polling the API for that is wasteful and slow, so the useful integration is a
 * webhook — which means the signing secret and the retry behaviour need to be explained
 * here rather than buried in docs a customer will not read until something breaks.
 */
export function Webhooks() {
  const qc = useQueryClient()
  const [url, setUrl] = useState('')
  const [description, setDescription] = useState('')
  const [created, setCreated] = useState<CreatedWebhook | null>(null)
  const [error, setError] = useState<ApiError | null>(null)

  const { data, isLoading } = useQuery({ queryKey: ['webhooks'], queryFn: api.webhooks.list })

  const create = useMutation({
    mutationFn: () => api.webhooks.create({
      url: url.trim(),
      description: description.trim() || undefined,
    }),
    onSuccess: async (w) => {
      setCreated(w)
      setUrl('')
      setDescription('')
      setError(null)
      await qc.invalidateQueries({ queryKey: ['webhooks'] })
    },
    onError: (e) => setError(e as ApiError),
  })

  const toggle = useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) =>
      api.webhooks.update(v.id, { enabled: v.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks'] }),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.webhooks.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['webhooks'] }),
  })

  // Inline edit: change the URL and/or which events an existing endpoint receives, without
  // deleting and re-adding it (which would rotate the signing secret the customer already wired up).
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editUrl, setEditUrl] = useState('')
  const [editEvents, setEditEvents] = useState<string[]>([])
  const [editError, setEditError] = useState<string | null>(null)

  const edit = useMutation({
    mutationFn: (v: { id: string; url: string; events: string[] }) =>
      api.webhooks.update(v.id, { url: v.url.trim(), events: v.events }),
    onSuccess: async () => {
      setEditingId(null)
      setEditError(null)
      await qc.invalidateQueries({ queryKey: ['webhooks'] })
    },
    onError: (e) => setEditError((e as ApiError).message),
  })

  function startEdit(id: string, url: string, events: string[]) {
    setEditingId(id); setEditUrl(url); setEditEvents(events); setEditError(null)
  }
  function toggleEditEvent(ev: string) {
    setEditEvents(list => list.includes(ev) ? list.filter(x => x !== ev) : [...list, ev])
  }

  const availableEvents = data?.available_events ?? []
  const hooks = data?.data ?? []

  return (
    <div>
      <h1>Webhooks</h1>
      <p className="lede">
        We POST delivery events to your endpoint as they happen. A bounce can arrive minutes
        after we accepted the message, so this is the only reliable way to keep your own
        records accurate.
      </p>

      <details className="panel" style={{ marginBottom: 20 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>How webhooks work — payload, events & signature</summary>
        <div style={{ marginTop: 14, display: 'grid', gap: 14 }}>
          <div>
            <div className="section-label">The request we POST to your URL</div>
            <pre className="code"><code>{`POST https://your-app.example.com/webhooks/itsbingmybusiness
Content-Type: application/json
X-Bookmyspot-Event: delivered
X-Bookmyspot-Signature: <hmac-sha256 hex of the raw body>

{
  "event_id": "evt_01M11ZK…",         // stable — deduplicate on this
  "type": "delivered",
  "occurred_at": "2026-08-27T15:40:00.000Z",
  "message_id": "msg_01M11XB9…",      // the message this event is about
  "recipient": "user@example.com",
  "smtp_code": "250",
  "dsn": "2.0.0",
  "response": "250 2.0.0 OK ... gsmtp",
  "url": null,
  "remote_mx": "gmail-smtp-in.l.google.com"
}`}</code></pre>
          </div>
          <div>
            <div className="section-label">Event types (each fires as a separate POST, in order)</div>
            <p className="small">
              <span className="mono">accepted</span> · <span className="mono">queued</span> · <span className="mono">delivered</span> · <span className="mono">deferred</span> · <span className="mono">bounced</span> · <span className="mono">complained</span> · <span className="mono">opened</span> · <span className="mono">clicked</span> · <span className="mono">unsubscribed</span> · <span className="mono">suppressed</span> · <span className="mono">blocked</span>
            </p>
          </div>
          <div>
            <div className="section-label">Verify & retries</div>
            <p className="small muted" style={{ margin: 0 }}>
              Verify authenticity: HMAC-SHA256 the raw body with your signing secret and compare to
              <span className="mono"> X-Bookmyspot-Signature</span>. Return <strong>2xx</strong> to acknowledge —
              otherwise we retry <span className="mono">0 → 1m → 5m → 15m → 1h → 3h → 6h → 12h → 24h</span> then
              dead-letter. Deduplicate on your side; every attempt is logged in delivery activity below.
            </p>
          </div>
        </div>
      </details>

      <div className="card" style={{ marginBottom: 24 }}>
        <form onSubmit={e => { e.preventDefault(); if (url.trim()) create.mutate() }}>
          <div className="field">
            <label htmlFor="wu">Endpoint URL</label>
            <input id="wu" placeholder="https://yourapp.com/hooks/email"
                   value={url} onChange={e => setUrl(e.target.value)} />
            <div className="hint">
              HTTPS only. Event payloads contain recipient addresses, and we will not send
              those over plaintext HTTP.
            </div>
          </div>
          <div className="field">
            <label htmlFor="wd">Description <span className="muted">(optional)</span></label>
            <input id="wd" placeholder="Production event handler"
                   value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          <button type="submit" disabled={!url.trim() || create.isPending}>
            {create.isPending ? 'Adding…' : 'Add endpoint'}
          </button>
        </form>

        {error && <div className="alert error" style={{ marginTop: 12, marginBottom: 0 }}>{error.message}</div>}
      </div>

      {created && (
        <div className="alert info reveal" style={{ marginBottom: 24 }}>
          <div>
            <strong>Endpoint added — copy the signing secret now.</strong>
            <p className="small" style={{ marginTop: 4 }}>
              Without verifying the signature, anyone who learns your URL can post fake
              delivery events to it. This secret is shown once.
            </p>
            <table className="kv boxed" style={{ marginTop: 12 }}>
              <tbody>
                <tr><td>URL</td><td className="mono small">{created.url}</td></tr>
                <tr>
                  <td>Signing secret</td>
                  <td className="mono"><strong>{created.signing_secret}</strong></td>
                </tr>
                <tr><td>Header</td><td className="mono small">{created.signature_header}</td></tr>
                <tr><td>Scheme</td><td className="small">{created.signature_scheme}</td></tr>
              </tbody>
            </table>
            <button className="secondary" style={{ marginTop: 12 }}
                    onClick={() => navigator.clipboard?.writeText(created.signing_secret)}>
              Copy secret
            </button>
            <button className="ghost" style={{ marginTop: 12, marginLeft: 8 }}
                    onClick={() => setCreated(null)}>
              I have saved it
            </button>
          </div>
        </div>
      )}

      {isLoading && <div className="skeleton" style={{ height: 140 }} />}

      {!isLoading && hooks.length === 0 && !created && (
        <div className="empty">
          <div className="big">⇄</div>
          <p><strong>No webhook endpoints</strong></p>
          <p>Add one to receive delivery, bounce and complaint events as they happen.</p>
        </div>
      )}

      {hooks.length > 0 && (
        <div className="card flush">
          {hooks.map(w => {
            let events: string[] = []
            try { events = JSON.parse(w.events) } catch { /* stored as JSON; ignore junk */ }

            const broken = w.auto_disabled_at || w.consecutive_failures >= 3

            return (
              <div key={w.id} className="hook-row">
                <div className="spread" style={{ alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div className="name mono truncate">{w.url}</div>
                    {w.description && <div className="small muted">{w.description}</div>}
                    <div className="chips">
                      {events.map(e => <span key={e} className="chip">{e}</span>)}
                    </div>
                  </div>
                  <div className="row" style={{ gap: 8, flexShrink: 0 }}>
                    <span className={`badge ${w.auto_disabled_at ? 'critical' : w.enabled ? 'ok' : 'pending'}`}>
                      {w.auto_disabled_at ? 'Auto-disabled' : w.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>

                <div className="hook-meta small muted">
                  <span>Last success {relTime(w.last_success_at)}</span>
                  {w.last_failure_at && <span>Last failure {relTime(w.last_failure_at)}</span>}
                  {w.consecutive_failures > 0 && (
                    <span className="crit">{w.consecutive_failures} consecutive failures</span>
                  )}
                </div>

                {broken && (
                  <div className="alert error" style={{ marginTop: 10, marginBottom: 0 }}>
                    <div>
                      <strong>This endpoint is failing.</strong>{' '}
                      {w.last_failure_reason ?? 'No response.'}
                      <p className="small" style={{ marginTop: 4, marginBottom: 0 }}>
                        We retry with backoff and stop after repeated failures. Continuing to
                        hammer a dead endpoint would delay everyone else's events, so the
                        breaker is deliberate — fix the endpoint, then re-enable.
                      </p>
                    </div>
                  </div>
                )}

                <div className="row" style={{ gap: 8, marginTop: 12 }}>
                  <button className="secondary"
                          onClick={() => editingId === w.id ? setEditingId(null) : startEdit(w.id, w.url, events)}>
                    {editingId === w.id ? 'Close' : 'Edit'}
                  </button>
                  <button className="secondary"
                          onClick={() => toggle.mutate({ id: w.id, enabled: !w.enabled })}>
                    {w.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button className="ghost danger"
                          onClick={() => {
                            if (confirm(`Remove ${w.url}?`)) remove.mutate(w.id)
                          }}>
                    Remove
                  </button>
                </div>

                {editingId === w.id && (
                  <div className="card" style={{ marginTop: 12, background: 'var(--surface-2, rgba(0,0,0,.02))' }}>
                    <div className="field">
                      <label htmlFor={`eu-${w.id}`}>Endpoint URL</label>
                      <input id={`eu-${w.id}`} value={editUrl} onChange={e => setEditUrl(e.target.value)}
                             placeholder="https://yourapp.com/hooks/email" />
                    </div>
                    <div className="field">
                      <label>Events</label>
                      <div className="chips" style={{ gap: 10, flexWrap: 'wrap' }}>
                        {availableEvents.map(ev => (
                          <label key={ev} className="row" style={{ gap: 5, cursor: 'pointer', fontSize: 13 }}>
                            <input type="checkbox" checked={editEvents.includes(ev)}
                                   onChange={() => toggleEditEvent(ev)} />
                            {ev}
                          </label>
                        ))}
                      </div>
                    </div>
                    {editError && <div className="alert error" style={{ marginTop: 8 }}>{editError}</div>}
                    <div className="row" style={{ gap: 8, marginTop: 10 }}>
                      <button disabled={!editUrl.trim() || editEvents.length === 0 || edit.isPending}
                              onClick={() => edit.mutate({ id: w.id, url: editUrl, events: editEvents })}>
                        {edit.isPending ? 'Saving…' : 'Save changes'}
                      </button>
                      <button className="ghost" onClick={() => setEditingId(null)}>Cancel</button>
                    </div>
                    <p className="small muted" style={{ margin: '8px 0 0' }}>
                      The signing secret does not change when you edit — only when you create a new endpoint.
                    </p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <h2>Verifying a payload</h2>
      <p className="small muted" style={{ marginTop: -8, marginBottom: 12 }}>
        Compute the HMAC over the <em>raw</em> body, before any JSON parsing. Parsing and
        re-serialising changes the bytes and the signature will never match.
      </p>
      <pre className="code"><code>{`import crypto from 'node:crypto'

app.post('/hooks/email', express.raw({ type: 'application/json' }), (req, res) => {
  const expected = crypto
    .createHmac('sha256', process.env.ITSBINGMYBUSINESS_WEBHOOK_SECRET)
    .update(req.body)               // the raw Buffer, not a parsed object
    .digest('hex')

  const given = req.get('X-Bookmyspot-Signature') ?? ''

  // Constant-time compare: a plain === leaks the secret one byte at a time
  // to anyone willing to measure the response.
  const ok = given.length === expected.length &&
    crypto.timingSafeEqual(Buffer.from(given), Buffer.from(expected))

  if (!ok) return res.sendStatus(401)

  const event = JSON.parse(req.body.toString())
  // Respond 2xx quickly, then process. We time out slow endpoints and retry,
  // which turns a slow handler into duplicate events.
  res.sendStatus(204)
  queue.push(event)
})`}</code></pre>

      <h2>Events you can subscribe to</h2>
      <div className="chips" style={{ marginTop: 4 }}>
        {(data?.available_events ?? []).map(e => <span key={e} className="chip">{e}</span>)}
      </div>
    </div>
  )
}
